import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import {
  addEligibleMember,
  getPimAutomationDetails,
  getPimAutomations,
  PimAutomationDetails,
  PimAutomationGroup,
  removeEligibleMember,
} from '@/api/adminApi';
import { searchUsers } from '@/services/resourcesApi';

interface SearchUserResult {
  id: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
}

export default function AutomatedElevationsPage() {
  const [loadingAutomations, setLoadingAutomations] = useState(true);
  const [automations, setAutomations] = useState<PimAutomationGroup[]>([]);
  const [automationSearch, setAutomationSearch] = useState('');
  const [selectedAutomationId, setSelectedAutomationId] = useState<number | null>(null);
  const [selectedAutomationDetails, setSelectedAutomationDetails] = useState<PimAutomationDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [userSearch, setUserSearch] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchUserResult[]>([]);
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAutomations();
  }, []);

  async function loadAutomations() {
    setLoadingAutomations(true);
    setError(null);
    try {
      const data = await getPimAutomations();
      setAutomations(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load automated PIM groups');
    } finally {
      setLoadingAutomations(false);
    }
  }

  async function loadAutomationDetails(m365AdminGroupId: number) {
    setSelectedAutomationId(m365AdminGroupId);
    setLoadingDetails(true);
    setError(null);
    try {
      const data = await getPimAutomationDetails(m365AdminGroupId);
      setSelectedAutomationDetails(data);
      setSearchResults([]);
      setUserSearch('');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load automation details');
    } finally {
      setLoadingDetails(false);
    }
  }

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  async function refreshAutomationDetailsWithRetry(
    m365AdminGroupId: number,
    isSettled: (details: PimAutomationDetails) => boolean,
    maxAttempts = 8,
  ): Promise<PimAutomationDetails | null> {
    let lastDetails: PimAutomationDetails | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const details = await getPimAutomationDetails(m365AdminGroupId);
      lastDetails = details;

      if (isSettled(details)) {
        setSelectedAutomationDetails(details);
        return details;
      }

      if (attempt < maxAttempts) {
        await delay(700);
      }
    }

    return lastDetails;
  }

  const filteredAutomations = useMemo(() => {
    const q = automationSearch.trim().toLowerCase();
    if (!q) {
      return automations;
    }

    return automations.filter((a) => {
      return (
        a.title.toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q) ||
        (a.schedule || '').toLowerCase().includes(q)
      );
    });
  }, [automations, automationSearch]);

  async function handleSearchUsers() {
    if (!userSearch.trim()) {
      return;
    }

    setSearchingUsers(true);
    try {
      const data = await searchUsers(userSearch.trim());
      setSearchResults(data as SearchUserResult[]);
    } catch {
      setSearchResults([]);
      setError('Failed to search users');
    } finally {
      setSearchingUsers(false);
    }
  }

  async function handleAddEligibleMember(userId: string) {
    if (!selectedAutomationId) {
      return;
    }

    const selectedUser = searchResults.find((u) => u.id === userId);

    setSelectedAutomationDetails((prev) => {
      if (!prev) {
        return prev;
      }

      const alreadyExists = prev.eligibleMembers.some((m) => m.id === userId);
      if (alreadyExists) {
        return prev;
      }

      return {
        ...prev,
        eligibleMembers: [
          ...prev.eligibleMembers,
          {
            id: userId,
            displayName: selectedUser?.displayName || selectedUser?.userPrincipalName || selectedUser?.mail || userId,
            email: selectedUser?.mail || selectedUser?.userPrincipalName || '',
          },
        ],
      };
    });

    setAddingUserId(userId);
    setError(null);
    try {
      await addEligibleMember(selectedAutomationId, userId);
      const refreshed = await refreshAutomationDetailsWithRetry(
        selectedAutomationId,
        (details) => details.eligibleMembers.some((m) => m.id === userId),
      );

      if (!refreshed || !refreshed.eligibleMembers.some((m) => m.id === userId)) {
        setError('Membership update is still syncing with Entra. The list may take a few seconds to fully reconcile.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to add eligible member');
      await loadAutomationDetails(selectedAutomationId);
    } finally {
      setAddingUserId(null);
    }
  }

  async function handleRemoveEligibleMember(userId: string) {
    if (!selectedAutomationId) {
      return;
    }

    setSelectedAutomationDetails((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        eligibleMembers: prev.eligibleMembers.filter((m) => m.id !== userId),
      };
    });

    setRemovingUserId(userId);
    setError(null);
    try {
      await removeEligibleMember(selectedAutomationId, userId);
      const refreshed = await refreshAutomationDetailsWithRetry(
        selectedAutomationId,
        (details) => !details.eligibleMembers.some((m) => m.id === userId),
      );

      if (refreshed && refreshed.eligibleMembers.some((m) => m.id === userId)) {
        setSelectedAutomationDetails((prev) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            eligibleMembers: prev.eligibleMembers.filter((m) => m.id !== userId),
          };
        });
        setError('Membership update is still syncing with Entra. The list may take a few seconds to fully reconcile.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove eligible member');
      await loadAutomationDetails(selectedAutomationId);
    } finally {
      setRemovingUserId(null);
    }
  }

  return (
    <div className="container-fluid mt-4">
      <div className="mb-3">
        <h2 className="mb-1">Automated Elevations</h2>
        <p className="text-muted mb-0">Manage PIM automated elevation group membership.</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-3">
        <Col lg={5}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Automated Elevations</strong>
              <Button variant="outline-secondary" size="sm" onClick={loadAutomations}>Refresh</Button>
            </Card.Header>
            <Card.Body>
              <Form.Control
                type="search"
                placeholder="Filter by title, description, or schedule"
                value={automationSearch}
                onChange={(e) => setAutomationSearch(e.target.value)}
                className="mb-3"
              />

              {loadingAutomations ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : filteredAutomations.length === 0 ? (
                <Alert variant="info" className="mb-0">No automated groups found.</Alert>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Scheduled</th>
                        <th>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAutomations.map((row) => (
                        <tr
                          key={row.m365AdminGroupId}
                          onClick={() => loadAutomationDetails(row.m365AdminGroupId)}
                          style={{ cursor: 'pointer' }}
                          className={selectedAutomationId === row.m365AdminGroupId ? 'table-primary' : ''}
                        >
                          <td>
                            <div>{row.title}</div>
                            <small className="text-muted">{row.schedule || 'No schedule'}</small>
                          </td>
                          <td>{row.isScheduled ? 'Yes' : 'No'}</td>
                          <td>{row.isActive ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card>
            <Card.Header>
              <strong>Group Membership Details</strong>
            </Card.Header>
            <Card.Body>
              {!selectedAutomationId ? (
                <Alert variant="secondary" className="mb-0">Select an automated elevation row to view active and eligible membership.</Alert>
              ) : loadingDetails ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : !selectedAutomationDetails ? (
                <Alert variant="warning" className="mb-0">No details were returned for this automation group.</Alert>
              ) : (
                <>
                  <div className="mb-3">
                    <h5 className="mb-1">{selectedAutomationDetails.group.title}</h5>
                    <div className="text-muted">{selectedAutomationDetails.group.description || 'No description'}</div>
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Card>
                        <Card.Header>Active Group Members ({selectedAutomationDetails.activeMembers.length})</Card.Header>
                        <Card.Body>
                          {selectedAutomationDetails.activeMembers.length === 0 ? (
                            <div className="text-muted">No active members found.</div>
                          ) : (
                            <div className="table-responsive">
                              <Table size="sm" hover>
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedAutomationDetails.activeMembers.map((m) => (
                                    <tr key={`active-${m.id}`}>
                                      <td>{m.displayName || '-'}</td>
                                      <td>{m.email || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card>
                        <Card.Header>Eligible Group Members ({selectedAutomationDetails.eligibleMembers.length})</Card.Header>
                        <Card.Body>
                          {selectedAutomationDetails.eligibleMembers.length === 0 ? (
                            <div className="text-muted mb-2">No eligible members found.</div>
                          ) : (
                            <div className="table-responsive mb-3">
                              <Table size="sm" hover>
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedAutomationDetails.eligibleMembers.map((m) => (
                                    <tr key={`eligible-${m.id}`}>
                                      <td>{m.displayName || '-'}</td>
                                      <td>{m.email || '-'}</td>
                                      <td className="text-end">
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => handleRemoveEligibleMember(m.id)}
                                          disabled={removingUserId === m.id}
                                        >
                                          {removingUserId === m.id ? 'Removing...' : 'Remove'}
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )}

                          <div className="border-top pt-3">
                            <Form.Label>Add user to eligible group</Form.Label>
                            <div className="d-flex gap-2 mb-2">
                              <Form.Control
                                type="text"
                                placeholder="Search by name or email"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchUsers();
                                  }
                                }}
                              />
                              <Button variant="outline-primary" onClick={handleSearchUsers} disabled={searchingUsers || !userSearch.trim()}>
                                {searchingUsers ? 'Searching...' : 'Search'}
                              </Button>
                            </div>

                            {searchResults.length > 0 && (
                              <div className="list-group">
                                {searchResults.map((u) => (
                                  <div key={u.id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <div>
                                      <div>{u.displayName || u.userPrincipalName || u.mail || u.id}</div>
                                      <small className="text-muted">{u.mail || u.userPrincipalName || ''}</small>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="success"
                                      onClick={() => handleAddEligibleMember(u.id)}
                                      disabled={addingUserId === u.id}
                                    >
                                      {addingUserId === u.id ? 'Adding...' : 'Add'}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
